import React from 'react';
import { useStaggeredReveal } from '../../hooks/useStaggeredReveal';
import { TONE_CLASSES } from '../analytics/chartTheme';

/** Show this many items before the "Show more" affordance kicks in. */
const INITIAL_VISIBLE = 6;

interface ActionsPanelProps {
  recommendedActions: string[];
  openQuestions: string[];
  /** Show at most this many of each, with no "Show more". Omit to reveal all. */
  limit?: number;
}

interface RevealListProps {
  /** The list to render. */
  items: string[];
  /** Leading marker for each row (bullet dot, "?" glyph, etc.). */
  marker: React.ReactNode;
}

/**
 * A list capped at {@link INITIAL_VISIBLE} items with a staggered "Show more"
 * reveal. Mirrors the objectives-step reveal in CreateInterviewForm: extras
 * fade in one-by-one rather than the panel jumping to full height.
 */
function RevealList({ items, marker }: RevealListProps) {
  const { visibleCount, canShowMore, hiddenCount, showMore } = useStaggeredReveal(
    items.length,
    INITIAL_VISIBLE,
  );

  return (
    <>
      <ul className="space-y-3">
        {items.slice(0, visibleCount).map((item, index) => (
          <li
            key={index}
            className={`flex items-start gap-3 ${index >= INITIAL_VISIBLE ? 'animate-fade-content' : ''}`}
          >
            {marker}
            <span className="text-sm text-foreground">{item}</span>
          </li>
        ))}
      </ul>
      {canShowMore && (
        <button
          type="button"
          onClick={showMore}
          className="mt-4 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Show {hiddenCount} more
        </button>
      )}
    </>
  );
}

/** One toned card of next steps or open questions. */
function ActionCard({ title, items, tone, marker, limit }: { title: string; items: string[]; tone: string; marker: React.ReactNode; limit?: number }) {
  return (
    <article className={`${tone} flex flex-col gap-4 rounded-[20px] p-6`}>
      <h3 className="text-[15px] font-medium text-foreground">{title}</h3>
      {limit === undefined ? <RevealList items={items} marker={marker} /> : (
        <ul className="space-y-3">
          {items.slice(0, limit).map((item, index) => (
            <li key={index} className="flex items-start gap-3">
              {marker}
              <span className="text-sm text-foreground">{item}</span>
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

/** Next steps and open questions as two toned cards, matching the Overview's card design. */
export function ActionsPanel({ recommendedActions, openQuestions, limit }: ActionsPanelProps) {
  if (!recommendedActions.length && !openQuestions.length) return null;
  return (
    <div className={`grid grid-cols-1 ${recommendedActions.length && openQuestions.length ? 'md:grid-cols-2' : ''} gap-4`}>
      {recommendedActions.length > 0 ? (
        <ActionCard title="Next steps" items={recommendedActions} tone={TONE_CLASSES.flow} limit={limit}
          marker={<span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--chart-mark)]" />} />
      ) : null}
      {openQuestions.length > 0 ? (
        <ActionCard title="Open questions" items={openQuestions} tone={TONE_CLASSES.signal} limit={limit}
          marker={<span className="mt-0.5 flex-shrink-0 font-mono text-sm text-[var(--chart-mark)]">?</span>} />
      ) : null}
    </div>
  );
}

export default ActionsPanel;
